import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Flex, HStack } from '@chakra-ui/react';

import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import SunshineTag from 'components/_core/SunshineTag';
import NavigationHeader from 'components/NavigationHeader';
import { logEvent } from 'features/Analytics/analytics';
import * as Layout from 'layouts/ContentLayout';

import * as routes from '../../../../routes';
import useHelpHome from '../../hooks/useHelpHome';
import locales from './locales';

export type HelpQuestionSidebarProps = {
  onClose: () => void;
};

const HelpQuestionSidebar: FC<HelpQuestionSidebarProps> = ({ onClose }) => {
  const { loading, phoneSupportLink, standardHotline } = useHelpHome();

  const localizedStandardHotline = standardHotline?.find(
    (hotline) => hotline?.language === locales.getLanguage(),
  );

  return (
    <Layout.Sidebar
      borderLeft="1px solid"
      borderLeftColor="border"
      isScrollable
    >
      <Flex flexDirection="column" paddingX="space-24" paddingY="space-16">
        <NavigationHeader onClose={onClose} />
        <Heading as="h2" mb="space-18" size="md">
          {locales.title}
        </Heading>

        <SunshineCard.Group isBoxed={false} marginBottom="space-16">
          <SunshineCard
            as={Link}
            onClick={() => logEvent({ name: 'Ask Question Help Menu' })}
            to={routes.askQuestion}
          >
            <SunshineCard.Slot name="aside">
              <SquircleIcon name="mail" />
            </SunshineCard.Slot>

            <SunshineCard.Content>
              <HStack>
                <SunshineCard.Title>{locales.Email.title}</SunshineCard.Title>
                <SunshineTag
                  flexShrink={0}
                  icon="timer"
                  noOfLines={1}
                  size="sm"
                >
                  {locales.Email.tag}
                </SunshineTag>
              </HStack>
              <SunshineCard.Text>{locales.Email.subtitle}</SunshineCard.Text>
            </SunshineCard.Content>

            <SunshineCard.Slot name="action">
              <SunshineCard.SunshineIcon name="chevron-right" />
            </SunshineCard.Slot>
          </SunshineCard>

          {!loading ? (
            <SunshineCard
              as="a"
              href={phoneSupportLink}
              onClick={() => logEvent({ name: 'Phone Support Help Menu' })}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="calendar" />
              </SunshineCard.Slot>

              <SunshineCard.Content>
                <HStack>
                  <SunshineCard.Title>{locales.Phone.title}</SunshineCard.Title>
                  <SunshineTag
                    flexShrink={0}
                    icon="timer"
                    noOfLines={1}
                    size="sm"
                  >
                    {locales.Phone.tag}
                  </SunshineTag>
                </HStack>
                <SunshineCard.Text>{locales.Phone.subtitle}</SunshineCard.Text>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>
          ) : null}

          {!loading && localizedStandardHotline ? (
            <SunshineCard
              as="a"
              href={`tel:${localizedStandardHotline.phoneNumber}`}
              onClick={() => logEvent({ name: 'Phone Support Help Menu' })}
              rel="noopener noreferrer"
              target="_blank"
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="phone" />
              </SunshineCard.Slot>

              <SunshineCard.Content>
                <SunshineCard.Title>
                  {localizedStandardHotline.label}
                </SunshineCard.Title>
                <SunshineCard.Text>
                  {localizedStandardHotline.sublabel}
                </SunshineCard.Text>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>
          ) : null}
        </SunshineCard.Group>
      </Flex>
    </Layout.Sidebar>
  );
};

export default HelpQuestionSidebar;
